// Cancel-with-attended-time (Session AQY, Director decision D-AQX-4). Shared by the Scheduling
// calendar's job-level cancel (SchedulingPage.tsx, via api.schedule.cancelJob) and the Call-outs
// page's own cancel (CalloutsPage.tsx, via api.callouts.cancel, which also cancels the linked
// dispatch job atomically when one exists) -- the host decides which API to call and passes it in as
// `onConfirm`, so this component owns only the "attended, then called off" question and its result.
//
// TWO SUB-CASES, in one small form: "not attended" (the default) needs no further input at all --
// press once, confirm, done. "Attended" reveals the travel-minutes field, because that is the one
// figure the worked example (an engineer 45 minutes into travelling, called back) actually needs;
// an arrival time is deliberately not offered here (this is an office-driven form filled in AFTER
// the fact from what the engineer reported over the phone, not a live on-site capture, so a single
// "how long were they on the road" figure is the honest level of detail to ask for).
import { useState } from "react";
import { ApiError } from "../../lib/api";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "../../components/ModalShell";
import { ConfirmButton } from "../../components/ConfirmButton";

export interface CancelJobResult {
  travelLogged: boolean;
  travelSkippedReason: string | null;
  /** MDF1: plain sentences about the engineer's other visits that day whose travel was not decided again, when there are any. */
  travelNotRechecked?: string[];
}

export function CancelJobModal({
  liftLabel,
  onClose,
  onConfirm,
}: {
  liftLabel: string; // e.g. "ELE-097-01 | Maple Court" -- the header context line
  onClose: () => void;
  // Calls whichever route the host owns (api.schedule.cancelJob or api.callouts.cancel) with the
  // body this modal built, and returns what actually happened.
  onConfirm: (body: { attended: boolean; travelMins?: number; notes?: string }) => Promise<CancelJobResult>;
}) {
  const [attended, setAttended] = useState(false);
  const [travelMins, setTravelMins] = useState("");
  const [notes, setNotes] = useState("");
  const [phase, setPhase] = useState<"form" | "saving" | "done">("form");
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CancelJobResult | null>(null);

  const busy = phase === "saving";

  async function confirmCancel() {
    setPhase("saving");
    setError(null);
    const mins = Number.parseInt(travelMins, 10);
    try {
      const res = await onConfirm({
        attended,
        ...(attended && Number.isFinite(mins) && mins > 0 ? { travelMins: mins } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      });
      setResult(res);
      setPhase("done");
    } catch (err) {
      setError(err instanceof ApiError && err.message ? err.message : "Could not cancel this visit. Please try again.");
      setPhase("form");
    }
  }

  return (
    <ModalShell ariaLabel="Cancel this visit" onClose={() => !busy && onClose()} width="max-w-md" panelClassName="p-6">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-xl text-ele-strong">Cancel this visit</h2>
          <p className="text-sm text-ele-body">{liftLabel}</p>
        </div>
        <button onClick={() => !busy && onClose()} aria-label="Close" className="text-ele-body hover:text-ele-green">
          ✕
        </button>
      </div>

      {phase === "done" && result ? (
        <>
          <p className="rounded-ele border border-ele-green bg-ele-surface px-3 py-2 text-sm text-ele-strong">
            Cancelled. The visit stays in the lift's history, marked as cancelled.
          </p>
          {attended && (
            <p className="mt-3 text-sm text-ele-body">
              {result.travelLogged
                ? "The engineer's travel time was recorded and has been added to their timesheet."
                : (result.travelSkippedReason ?? "No travel time was logged.")}
            </p>
          )}
          {(result.travelNotRechecked ?? []).map((note) => (
            <p key={note} role="status" className="mt-3 rounded-ele border border-ele-purple-accent bg-ele-surface px-3 py-2 text-sm text-ele-strong">
              {note}
            </p>
          ))}
          <div className={`mt-5 gap-3 ${MODAL_ACTIONS}`}>
            <button onClick={onClose} className={`rounded-ele border border-ele-purple-accent px-4 py-2 text-sm ${MODAL_ACTION_BTN}`}>
              Close
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="mb-4 text-sm text-ele-body">
            This keeps the visit in the lift's history, marked as cancelled, unlike Delete, which removes it entirely. Use
            this for a genuine customer cancellation.
          </p>

          <fieldset className="space-y-2">
            <legend className="mb-1 font-menu text-xs uppercase tracking-wide text-ele-body">
              Had the engineer already set off or arrived?
            </legend>
            <label className="flex items-center gap-2 text-sm text-ele-strong">
              <input type="radio" checked={!attended} onChange={() => setAttended(false)} disabled={busy} />
              No: cancelled before any travel
            </label>
            <label className="flex items-center gap-2 text-sm text-ele-strong">
              <input type="radio" checked={attended} onChange={() => setAttended(true)} disabled={busy} />
              Yes: travelling or on site when it was called off
            </label>
          </fieldset>

          {attended && (
            <label className="mt-3 block">
              <span className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body">
                Travel time before the call-off (minutes)
              </span>
              <input
                type="number"
                min={1}
                max={1440}
                value={travelMins}
                onChange={(e) => setTravelMins(e.target.value)}
                placeholder="e.g. 45"
                disabled={busy}
                className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green disabled:opacity-50"
              />
              <span className="mt-1 block text-xs text-ele-body opacity-70">
                Logged to the engineer's own timesheet, billed to ELE, never the customer.
              </span>
            </label>
          )}

          <label className="mt-3 block">
            <span className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body">Notes (optional)</span>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Why the customer cancelled"
              disabled={busy}
              className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green disabled:opacity-50"
            />
          </label>

          {error && <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">{error}</p>}

          <div className={`mt-5 gap-3 sm:items-center ${MODAL_ACTIONS}`}>
            <ConfirmButton
              onConfirm={confirmCancel}
              label="Cancel visit"
              confirmLabel="Press again to confirm"
              busy={busy}
              busyLabel="Cancelling..."
              className={`rounded-ele border border-ele-danger px-4 py-2 font-heading text-sm tracking-wide text-ele-danger transition hover:bg-ele-danger/10 disabled:opacity-50 ${MODAL_ACTION_BTN}`}
            />
            <button onClick={() => !busy && onClose()} disabled={busy} className={`rounded-ele border border-ele-purple-accent px-4 py-2 text-sm disabled:opacity-50 ${MODAL_ACTION_BTN}`}>
              Close
            </button>
          </div>
        </>
      )}
    </ModalShell>
  );
}
